import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@apollo/client";
import { backendClient } from "@/lib/backendClient";
import { useSelectionStore } from "@/store/selection";
import { useOrderStore } from "@/store/order";
import { useSessionStore } from "@/store/session";
import { useProducts } from "@/models/product";
import type { ProductType } from "@/models/product";
import { parseSunProtectionOrderByIdResponse } from "../mappers/order.parse";
import { hydrateSelectionFromOrder } from "../mappers/hydrate";
import GET_SUN_PROTECTION_ORDER_BY_ID from "../graphql/GetSunProtectionOrderById.graphql";

type UseOrderByIdArgs = {
    orderId: string | null;
    hubspotDealId: number | null;
};

type UseOrderByIdResult = {
    loading: boolean;
    error: Error | null;
};

export function useOrderById({
    orderId,
    hubspotDealId,
}: UseOrderByIdArgs): UseOrderByIdResult {
    const hasHydrated = useRef(false);
    const { data, loading, error } = useQuery<unknown>(
        GET_SUN_PROTECTION_ORDER_BY_ID,
        {
            client: backendClient,
            variables: { orderId, hubspotDealId },
            skip: !orderId || hubspotDealId == null,
        },
    );

    const order = useMemo(
        () => parseSunProtectionOrderByIdResponse(data),
        [data],
    );
    const activeCategory: ProductType | null =
        order?.products[0]?.productType ?? null;
    const { products } = useProducts(activeCategory);

    useEffect(() => {
        if (hasHydrated.current) return;
        if (!order) return;
        const hydrated = hydrateSelectionFromOrder(order, products);
        if (!hydrated) return;

        useSelectionStore.setState({
            selectedCategory: hydrated.selectedCategory,
            selectedTypeId: hydrated.selectedTypeId,
            selectedProductIds: hydrated.activeSnapshot.selectedProductIds,
            windowSelections: hydrated.activeSnapshot.windowSelections,
            cachedByCombo: hydrated.cachedByCombo,
            lastTypeIdByCategory: hydrated.lastTypeIdByCategory,
        });

        if (order.signature?.url) {
            useOrderStore.getState().setSignatureUrl(order.signature.url);
        }
        if (order.discountCode?.code) {
            useSessionStore.getState().setDiscountCode(order.discountCode.code);
        }
        useOrderStore.getState().setOrderId(order.id);

        hasHydrated.current = true;
    }, [order, products]);

    const querySettled = !loading && error === undefined && data !== undefined;
    const notFound = querySettled && order === null;

    return {
        loading,
        error:
            (error as Error | undefined) ??
            (notFound ? new Error("order-not-found") : null),
    };
}
